import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { apiUpload } from './api';

/**
 * Drop files anywhere on a page to attach them to the page's item (#321):
 * an email dragged out of Outlook, a PDF from Explorer. Drops something else
 * already handled (an image into the editor) are left alone. Each upload is
 * announced with ATTACHMENT_ADDED_EVENT so the item's file list shows it.
 */

export const ATTACHMENT_ADDED_EVENT = 'dossier:attachment-added';

export interface AttachmentAddedDetail {
  /** The upload's parent field, e.g. 'meeting_instance_id'. */
  parentKey: string;
  parentId: string;
  attachment: { id: string } & Record<string, unknown>;
}

const hasFiles = (dt: DataTransfer | null) => !!dt && Array.from(dt.types ?? []).includes('Files');

export function usePageFileDrop(parentKey: string, parentId: string, opts: { onError: (message: string) => void; onUploaded?: (name: string) => void }) {
  const [dragging, setDragging] = useState(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // However a drag ends - dropped on a child drop zone that stops it bubbling
  // (the Attachments box), dropped elsewhere, cancelled with Esc, or dragged
  // out of the window - the hint must go. Listening on the window in the
  // capture phase sees all of these before any child can stop them.
  useEffect(() => {
    const end = () => setDragging(false);
    const leave = (e: globalThis.DragEvent) => {
      if (e.relatedTarget) return;
      // Leaving the window has no related target and a point at or past its edge.
      const inside = e.clientX > 0 && e.clientY > 0 && e.clientX < window.innerWidth && e.clientY < window.innerHeight;
      if (!inside) setDragging(false);
    };
    window.addEventListener('drop', end, true);
    window.addEventListener('dragend', end, true);
    window.addEventListener('dragleave', leave, true);
    return () => {
      window.removeEventListener('drop', end, true);
      window.removeEventListener('dragend', end, true);
      window.removeEventListener('dragleave', leave, true);
    };
  }, []);

  const onDragOver = useCallback((e: DragEvent) => {
    if (!hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e: DragEvent) => {
    // Leaving for a child is not leaving the page.
    if (e.relatedTarget && (e.currentTarget as Node).contains(e.relatedTarget as Node)) return;
    setDragging(false);
  }, []);

  const onDrop = useCallback((e: DragEvent) => {
    setDragging(false);
    if (e.defaultPrevented || !hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    for (const file of Array.from(e.dataTransfer.files)) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append(parentKey, parentId);
      apiUpload<AttachmentAddedDetail['attachment']>('/attachments', fd)
        .then((attachment) => {
          window.dispatchEvent(new CustomEvent<AttachmentAddedDetail>(ATTACHMENT_ADDED_EVENT, { detail: { parentKey, parentId, attachment } }));
          optsRef.current.onUploaded?.(file.name);
        })
        .catch((err) => optsRef.current.onError(`${file.name}: ${err instanceof Error ? err.message : 'Upload failed'}`));
    }
  }, [parentKey, parentId]);

  return { dragging, bind: { onDragOver, onDragLeave, onDrop } };
}
